import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Toast, type ToastMessage, type ToastTone } from './Feedback';

interface ToastApi {
  toast: (message: string, tone?: ToastTone) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** At most this many on screen; older ones drop off rather than stacking forever. */
const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => {
    const push = (message: string, tone: ToastTone = 'info') => {
      counter.current += 1;
      const entry: ToastMessage = { id: `toast-${counter.current}`, tone, message };
      setToasts((current) => [...current, entry].slice(-MAX_VISIBLE));
    };

    return {
      toast: push,
      success: (message: string) => push(message, 'success'),
      error: (message: string) => push(message, 'error'),
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          // `polite` so a confirmation never interrupts what someone is reading.
          aria-live="polite"
          className="pointer-events-none fixed bottom-5 right-5 z-[60] flex w-full max-w-[360px] flex-col gap-2"
        >
          {toasts.map((entry) => (
            <div key={entry.id} className="pointer-events-auto">
              <Toast toast={entry} onDismiss={dismiss} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/**
 * Confirmation for actions whose result is not obvious on screen.
 *
 * Dragging a card between columns, saving a record, deleting a row — the change
 * happens somewhere the eye may not be. Do not toast things the UI already
 * shows plainly.
 */
export function useToast(): ToastApi {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used inside a ToastProvider.');
  }

  return context;
}
